import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ANNOUNCEMENTS } from '../data/announcements';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';

// Local calendar date as YYYY-MM-DD so it matches the stored value.
function toDateKey(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// Upcoming weekend: this Saturday/Sunday, or next weekend once the current one has passed.
function upcomingWeekend(now = new Date()) {
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysToSat = (6 - base.getDay() + 7) % 7;
  const sat = new Date(base);
  sat.setDate(base.getDate() + daysToSat);
  const sun = new Date(sat);
  sun.setDate(sat.getDate() + 1);
  return [
    { key: toDateKey(sat), date: sat },
    { key: toDateKey(sun), date: sun },
  ];
}

// "13:00" -> "1 PM", "13:30" -> "1:30 PM"
function formatTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${ap}` : `${h12} ${ap}`;
}

// Shows announcements for this agent's organization at the top of the dashboard,
// plus open houses on the upcoming weekend that still need a host.
// orgKey: 'residence' or 'rise'.
export default function AnnouncementFeed({ orgKey = 'residence' }) {
  const posts = ANNOUNCEMENTS.filter(
    (a) => a.audience === 'both' || a.audience === orgKey
  );

  const weekend = useMemo(() => upcomingWeekend(), []);
  const weekendKeys = weekend.map((w) => w.key);
  const [openHouses, setOpenHouses] = useState([]);

  useEffect(() => {
    const unsub = onValue(
      ref(database, 'openHouses'),
      (snap) => {
        const val = snap.val() || {};
        setOpenHouses(Object.entries(val).map(([id, v]) => ({ id, ...v })));
      },
      (err) => console.error('Could not load open house coverage', err)
    );
    return () => unsub();
  }, []);

  const needsCoverage = openHouses
    .filter((o) => o.coverageStatus === 'needed' && weekendKeys.includes(o.date))
    .sort((a, b) => (a.date + (a.startTime || '')).localeCompare(b.date + (b.startTime || '')));

  if (posts.length === 0 && needsCoverage.length === 0) return null;

  const dayShort = (key) => {
    const match = weekend.find((w) => w.key === key);
    return match ? match.date.toLocaleDateString('en-US', { weekday: 'short' }) : key;
  };

  return (
    <section style={{ marginBottom: '2rem', display: 'grid', gap: '1.25rem' }}>
      <h2 style={{ margin: 0, fontSize: '13px', letterSpacing: '.18em', textTransform: 'uppercase', color: '#8c6d1f' }}>
        Announcements
      </h2>

      {needsCoverage.length > 0 && (
        <article
          style={{
            background: '#ffffff',
            border: '1px solid #e6dcc3',
            borderTop: '3px solid #D4AF37',
            borderRadius: '16px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
            padding: '18px 22px',
          }}
        >
          <div style={{ fontSize: '12px', color: '#7f7f7f', letterSpacing: '.08em', textTransform: 'uppercase' }}>
            Open houses needing a host this weekend
          </div>
          {needsCoverage.map((o) => (
            <div
              key={o.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                borderTop: '1px solid #efefef',
                marginTop: '12px',
                paddingTop: '12px',
              }}
            >
              <p style={{ margin: 0, color: '#3a3a3a', lineHeight: 1.6, flex: '1 1 260px', minWidth: 0 }}>
                <strong>{o.agentName}</strong> needs an open house host on {dayShort(o.date)}
                {o.startTime ? ` ${formatTime(o.startTime)}` : ''}
                {o.endTime ? `-${formatTime(o.endTime)}` : ''} for {o.address}.
              </p>
              <Link
                to="/open-houses"
                style={{
                  display: 'inline-block',
                  padding: '10px 16px',
                  background: '#D4AF37',
                  color: '#1a1a1a',
                  borderRadius: '6px',
                  fontWeight: 700,
                  fontSize: '13px',
                  textDecoration: 'none',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                View and claim
              </Link>
            </div>
          ))}
        </article>
      )}

      {posts.map((p) => (
        <article
          key={p.id}
          style={{
            background: '#ffffff',
            border: '1px solid #e6dcc3',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
          }}
        >
          {p.image && (
            <img
              src={p.image}
              alt={p.title}
              style={{ display: 'block', width: '100%', maxWidth: '100%', height: 'auto' }}
            />
          )}
          <div style={{ padding: '18px 22px 22px' }}>
            <div style={{ fontSize: '12px', color: '#7f7f7f', letterSpacing: '.08em' }}>
              {new Date(p.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <h3 style={{ margin: '6px 0 8px', color: '#1a1a1a', fontSize: '22px' }}>{p.title}</h3>
            <p style={{ margin: 0, color: '#3a3a3a', lineHeight: 1.6 }}>{p.body}</p>
            {p.cta && (
              <p style={{ margin: '12px 0 0', color: '#8c6d1f', fontWeight: 600 }}>{p.cta}</p>
            )}
          </div>
        </article>
      ))}
    </section>
  );
}
